import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/dashboard/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Heart, Shield, ArrowRight, Activity, AlertTriangle, 
  Users, TrendingUp, FileText, Download, Calendar, Stethoscope
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { healthtrackApi } from '../../services/healthtrackApi';
import logger from '../../utils/logger';

const MainDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);

  const isPatient = user?.role === 'user' || user?.role === 'patient';
  const isDoctor = user?.role === 'doctor';

  // Redirect admins and superadmins to admin dashboard (only if on main dashboard)
  useEffect(() => {
    if ((user?.role === 'admin' || user?.role === 'superadmin') && window.location.pathname === '/dashboard') {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [user, navigate]);

  // Fetch role-appropriate stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoadingStats(true);
        if (isDoctor) {
          const [patientsData, apptsData] = await Promise.all([
            healthtrackApi.patients.list(0, 1).catch(() => ({ total: 0 })),
            healthtrackApi.appointments.list(0, 1).catch(() => ({ total: 0 })),
          ]);
          setStats({
            totalPatients: patientsData.total || 0,
            appointments: apptsData.total || 0,
          });
        } else if (isPatient) {
          const userData = await healthtrackApi.auth.me().catch(() => null);
          const patientsData = await healthtrackApi.patients.list(0, 50, userData?.email).catch(() => ({ patients: [] }));
          const patient = patientsData.patients?.[0];
          let myAppointments = 0;
          let myLabReports = 0;
          if (patient) {
            const [apptsData, labData] = await Promise.all([
              healthtrackApi.patients.getAppointments(patient.id).catch(() => ({ appointments: [] })),
              healthtrackApi.labTests.list(0, 50, patient.id).catch(() => ({ lab_tests: [] })),
            ]);
            myAppointments = apptsData.appointments?.length || 0;
            myLabReports = labData.lab_tests?.length || 0;
          }
          setStats({
            myAppointments,
            myLabReports,
          });
        }
      } catch (err) {
        logger.error('Failed to fetch stats:', err);
      } finally {
        setLoadingStats(false);
      }
    };
    if (user) fetchStats();
    // eslint-disable-next-line
  }, [user]);

  return (
    <DashboardLayout title="Dashboard Overview">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-orange-600 to-yellow-600 rounded-2xl p-8 text-white mb-8">
        <h2 className="text-3xl font-bold mb-2">
          Welcome back, {user?.name}! 👋
        </h2>
        <p className="text-orange-100 text-lg">
          Here's what's happening with your platforms today
        </p>
      </div>

      {/* Patient Stats - Individual patient data only */}
      {isPatient && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card data-testid="stat-health-score">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">My Health Score</p>
                  <p className="text-3xl font-bold text-green-600">--</p>
                  <p className="text-xs text-gray-400 mt-1">Complete assessment to calculate</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Activity className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-my-appointments">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">My Appointments</p>
                  <p className="text-3xl font-bold text-purple-600">
                    {loadingStats ? '...' : (stats?.myAppointments ?? 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-my-lab-reports">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">My Lab Reports</p>
                  <p className="text-3xl font-bold text-orange-600">
                    {loadingStats ? '...' : (stats?.myLabReports ?? 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <FileText className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Doctor Stats - Aggregate clinic data */}
      {isDoctor && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card data-testid="stat-health-score-doctor">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Health Score</p>
                  <p className="text-3xl font-bold text-green-600">85%</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Activity className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-total-patients">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Patients</p>
                  <p className="text-3xl font-bold text-blue-600">
                    {loadingStats ? '...' : (stats?.totalPatients ?? 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-appointments-doctor">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Appointments</p>
                  <p className="text-3xl font-bold text-purple-600">
                    {loadingStats ? '...' : (stats?.appointments ?? 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Stethoscope className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-lab-reports-doctor">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Lab Reports</p>
                  <p className="text-3xl font-bold text-orange-600">8</p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <FileText className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Admin sees both health and security stats */}
      {user?.role === 'admin' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Health Score</p>
                  <p className="text-3xl font-bold text-green-600">85%</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Activity className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Security Threats</p>
                  <p className="text-3xl font-bold text-red-600">3</p>
                </div>
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Active Devices</p>
                  <p className="text-3xl font-bold text-blue-600">12</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Growth</p>
                  <p className="text-3xl font-bold text-purple-600">+23%</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Products Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* HealthTrack Pro Card - Show to all users */}
        {(isPatient || isDoctor || user?.role === 'admin') && (
          <Card className="border-2 border-orange-200 hover:shadow-xl transition-all">
            <CardHeader className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center">
                  <Heart className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <CardTitle className="text-white">HealthTrack Pro</CardTitle>
                  <CardDescription className="text-orange-100">
                    AI-Powered Health Management
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Health Records</span>
                  <span className="font-semibold">12 records</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Upcoming Appointments</span>
                  <span className="font-semibold">2 pending</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Lab Reports</span>
                  <span className="font-semibold">3 analyzed</span>
                </div>
                <Link to="/dashboard/health">
                  <Button className="w-full mt-4 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700">
                    Open Dashboard
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Common tasks and shortcuts</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Pitch Deck - ONLY for Admin */}
            {user?.role === 'admin' && (
              <Link to="/pitch-deck">
                <Button variant="outline" className="w-full h-20 flex flex-col items-center justify-center gap-2">
                  <FileText className="w-6 h-6" />
                  <span>View Pitch Deck</span>
                </Button>
              </Link>
            )}
            
            {/* Desktop Apps - For all users */}
            <Link to="/downloads">
              <Button variant="outline" className="w-full h-20 flex flex-col items-center justify-center gap-2">
                <Download className="w-6 h-6" />
                <span>Desktop Apps</span>
              </Button>
            </Link>
            
            {/* Upload Lab Report - For health users */}
            <Link to="/dashboard/health">
              <Button variant="outline" className="w-full h-20 flex flex-col items-center justify-center gap-2">
                <Heart className="w-6 h-6" />
                <span>Upload Lab Report</span>
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </DashboardLayout>
  );
};

export default MainDashboard;
